import { CellData, NO_DATA_CELL, ProgressBar } from './ProgressBar';
import {
  formatDateMMddYYYY,
  MetricProgressBar,
  toProgressSegments,
} from '../utils/utils';
import {
  ProgressPortraitMetrics,
  VinelandAssessment,
} from '../model/ProgressPortrait';
import { CellTooltip, TooltipItem } from './CellTooltip';

const vinelandExtractor = (progressPortraitMetrics: ProgressPortraitMetrics) =>
  progressPortraitMetrics.vinelands ?? [];

const toCellData = (vineland: VinelandAssessment): CellData => {
  const iscore = vineland.MBI_Internalizing_Score__c;
  const escore = vineland.MBI_Externalizing_Score__c;
  let level: string;

  if (!(iscore && escore)) {
    level = 'N/A';
  } else if (!escore) {
    level = vineland.MBI_Internalizing_Adaptive_Level__c;
  } else if (!iscore) {
    level = vineland.MBI_Externalizing_Adaptive_Level__c;
  } else {
    level =
      iscore > escore
        ? vineland.MBI_Internalizing_Adaptive_Level__c
        : vineland.MBI_Externalizing_Adaptive_Level__c;
  }
  const date = vineland.vineland_Assessment_Date__c;
  if ((!level && !date) || level === 'N/A') {
    return NO_DATA_CELL;
  }
  const tooltip = <ProblemBehaviorTooltip vineland={vineland} />;
  if (level === 'Clinically Significant') {
    return {
      label: level,
      level: 'low',
      tooltip,
    };
  }
  if (level === 'Elevated') {
    return {
      label: level,
      level: 'medium',
      tooltip,
    };
  }
  return {
    label: 'Average',
    level: 'high',
    tooltip,
  };
};

export const ProblemBehaviorMetric = ({
  progressPortraitMetrics,
}: MetricProgressBar) => {
  const segments = toProgressSegments(
    progressPortraitMetrics,
    vinelandExtractor,
    toCellData,
  );
  return <ProgressBar segments={segments} />;
};

const ProblemBehaviorTooltip = ({
  vineland,
}: {
  vineland: VinelandAssessment;
}) => {
  const iscore = vineland.MBI_Internalizing_Score__c;
  const escore = vineland.MBI_Externalizing_Score__c;

  const internalTextTooltip = iscore
    ? `${iscore} (${vineland.MBI_Internalizing_Adaptive_Level__c})`
    : 'No Data';

  const externalTextTooltip = escore
    ? `${escore} (${vineland.MBI_Externalizing_Adaptive_Level__c})`
    : 'No Data';

  const info: TooltipItem[] = [
    {
      label: 'Vineland-3 Date:',
      value: formatDateMMddYYYY(vineland.vineland_Assessment_Date__c),
    },
    {
      label: 'Internalizing Score:',
      value: internalTextTooltip,
    },
    {
      label: 'Externalizing Score:',
      value: externalTextTooltip,
    },
    {
      label: 'Respondent:',
      value: vineland.name_Of_Respondent__c,
    },
  ];
  return <CellTooltip info={info} />;
};
